/**
 * progressiveFetch — runs a list of independent fetches in parallel and
 * surfaces results as they arrive instead of waiting for the slowest one.
 *
 * The old pattern across the dashboards looked like:
 *
 *   setLoading(true);
 *   const [aRes, bRes, cRes] = await Promise.all([fetch(...), ...]);
 *   const [aData, bData, cData] = await Promise.all([aRes.json(), ...]);
 *   if (aData.success) setA(aData.data);
 *   if (bData.success) setB(bData.data);
 *   if (cData.success) setC(cData.data);
 *   setLoading(false);
 *
 * That blocks the entire page on the SLOWEST endpoint. If the AI brief
 * or a heavy aggregation takes 5–10 seconds, the user stares at a
 * spinner for that whole time even though everything else is ready.
 *
 * This helper rewires the pattern so:
 *   - Each request lands in state independently the moment it resolves
 *     (so fast tiles paint while slow tiles are still loading)
 *   - One failed endpoint doesn't take down the rest (per-endpoint catch)
 *   - The page-level "loading" flag clears on the FIRST response, not
 *     the last
 *   - A safety cap fires the off-spinner anyway after `safetyMs` so a
 *     total stall still hands the user a (mostly empty) page
 *
 * Usage:
 *
 *   progressiveFetch({
 *     onFirstResponse: () => setLoading(false),
 *     onAllSettled: () => setFetchedAt(new Date()),
 *     safetyMs: 3000,
 *     tasks: [
 *       { url: '/api/foo', auth: token, onData: (d) => d.success && setFoo(d.data) },
 *       { url: '/api/bar', auth: token, onData: (d) => d.success && setBar(d.data) },
 *     ],
 *   });
 */

export function progressiveFetch({
  tasks = [],
  onFirstResponse,
  onAllSettled,
  safetyMs = 3000,
  logPrefix = '[progressiveFetch]',
} = {}) {
  if (!tasks.length) return Promise.resolve();

  // Coordination: only call onFirstResponse once, regardless of which
  // endpoint wins the race.
  let firstFulfilled = false;
  const markRendered = () => {
    if (firstFulfilled) return;
    firstFulfilled = true;
    try { onFirstResponse?.(); } catch (e) { console.error(`${logPrefix} onFirstResponse error:`, e); }
  };

  // Even if EVERY endpoint stalls, the safety timer guarantees the page
  // paints within safetyMs. The user gets a (mostly empty) layout with
  // section-level loading states, which is dramatically better UX than
  // a frozen page-level spinner.
  const safetyTimer = setTimeout(markRendered, safetyMs);

  const promises = tasks.map((task) => {
    const { url, fetchOptions, auth, onData, raw } = task;
    const headers = {};
    if (auth) headers.Authorization = `Bearer ${auth}`;
    if (fetchOptions?.headers) Object.assign(headers, fetchOptions.headers);

    return fetch(url, { ...fetchOptions, headers })
      .then((r) => (raw ? r : r.json()))
      .then((data) => {
        try {
          onData?.(data);
        } catch (e) {
          console.error(`${logPrefix} onData handler for ${url} threw:`, e);
        }
        return data;
      })
      .catch((e) => {
        console.error(`${logPrefix} ${url} failed:`, e);
        return null;
      });
  });

  // First endpoint home flips the spinner off — every kind of resolution
  // counts (including the errors we caught above) so a totally failing
  // first call still hands control back to the page.
  Promise.race(promises).then(markRendered);

  // Final cleanup once everything has settled.
  return Promise.allSettled(promises).finally(() => {
    clearTimeout(safetyTimer);
    try { onAllSettled?.(); } catch (e) { console.error(`${logPrefix} onAllSettled error:`, e); }
  });
}
